import type { PageSectionBackground } from '@athena-lib/container-context.svelte';
import { getResponsiveClasses, type MaybeResponsive } from '@athena-lib/responsive-utils';
import { createSafeCaster } from '@athena-lib/safeCaster';
import {
  leftPaddings,
  responsiveLeftPaddings,
  responsiveRightPaddings,
  rightPaddings,
} from '@athena-lib/spacing';
import clsx from 'clsx';

import { type Snippet } from 'svelte';
import type { Action } from 'svelte/action';

export type PageProps = {
  children: Snippet;
  footer?: Snippet;
  reloadOnNavigate?: boolean;
  class?: string;
};

export type PageSectionWidth = 'full' | 'large' | 'medium' | 'narrow';
const pageSectionYSpaceOptions = [
  'gutter',
  'small',
  'medium',
  'large',
  'xlarge',
  'none',
] as const satisfies Array<string>;
export const pageSectionYSpaceCaster = createSafeCaster(pageSectionYSpaceOptions);
export type PageSectionYSpace = (typeof pageSectionYSpaceOptions)[number];

const pageSectionXSpaceOptions = ['gutter', 'none'] as const satisfies Array<string>;
export const pageSectionXSpaceCaster = createSafeCaster(pageSectionXSpaceOptions);
export type PageSectionXSpace = (typeof pageSectionXSpaceOptions)[number];

export type PageSectionProps = {
  class?: string;
  innerClass?: string;
  backgroundColor?: MaybeResponsive<PageSectionBackground>;
  backgroundMediaUrl?: MaybeResponsive<string>;
  backgroundMediaFocalPointClass?: string;
  fullWidthBackgroundMedia?: MaybeResponsive<boolean>;
  fullWidthBackgroundColor?: MaybeResponsive<boolean>;
  width?: MaybeResponsive<PageSectionWidth>;
  children?: Snippet;
  action?: Action<HTMLDivElement, NonNullable<unknown>>;
  actionArg?: NonNullable<unknown>;
  ySpace?:
    | MaybeResponsive<PageSectionYSpace>
    | {
        top: MaybeResponsive<PageSectionYSpace>;
        bottom: MaybeResponsive<PageSectionYSpace>;
      };
  xSpace?:
    | PageSectionXSpace
    | {
        left?: PageSectionXSpace;
        right?: PageSectionXSpace;
      };
  id?: string;
};

export type InnerPageSectionProps = {
  class?: string;
  children: Snippet;
  width?: MaybeResponsive<PageSectionWidth>;
  innerClass?: string;
  backgroundColor?: MaybeResponsive<PageSectionBackground>;
  backgroundMediaUrl?: string;
  fullWidthBackgroundMedia?: boolean;
  fullWidthBackgroundColor?: boolean;
  xSpace?: 'none';
};

export const getPageSectionXMargin = (
  width: MaybeResponsive<PageSectionWidth>,
  backgroundImageStyle: MaybeResponsive<string> | undefined = undefined,
  fullWidthBackgroundMedia: MaybeResponsive<boolean> = false,
  fullWidthBackgroundColor: MaybeResponsive<boolean> = false,
) => {
  const fullWidthBackground = backgroundImageStyle
    ? fullWidthBackgroundMedia
    : fullWidthBackgroundColor;

  const fullWidthInMobile =
    (typeof width === 'string' && width !== 'full') ||
    (typeof width !== 'string' && width.sm !== 'full');

  const fullWidthMediaInMobile = isFalseInMobile(fullWidthBackground);

  // We only need to apply margin in mobile when it's not full width
  // A page section usually only have one X spacing but sometimes we
  // want two X spacings. One for whitespace (which this margin sets)
  // and then one spacing for the inner element which then has a background
  // color or image. This creates a white border around the inner element.
  return clsx(
    fullWidthMediaInMobile &&
      fullWidthInMobile &&
      // If there's a background image and it's not full width, add some margin to create a white border around
      // the image in mobile.
      'ml-medium mr-medium md:ml-auto md:mr-auto',
  );
};

const isFalseInMobile = (value: MaybeResponsive<boolean>) =>
  (typeof value === 'boolean' && !value) || (typeof value !== 'boolean' && !value.sm);

export const getPageSectionXPadding = (width: MaybeResponsive<PageSectionWidth>) => {
  const isFullWidthInLargerScreen =
    width === 'full' || (typeof width !== 'string' && width.lg === 'full');

  return clsx(
    leftPaddings.medium,
    isFullWidthInLargerScreen
      ? `${responsiveLeftPaddings.md_none}`
      : `${leftPaddings.medium} ${responsiveLeftPaddings.lg_gutter} ${responsiveLeftPaddings.md_medium}`,
    rightPaddings.medium,
    isFullWidthInLargerScreen
      ? `${responsiveRightPaddings.md_none}`
      : `${rightPaddings.medium} ${responsiveRightPaddings.lg_gutter} ${responsiveRightPaddings.md_medium}`,
  );
};

export const getPageWidthClass = (width: MaybeResponsive<PageSectionWidth>) =>
  getResponsiveClasses(width, pageWidths);

const pageWidths = {
  default: {
    large: 'max-w-page-max',
    medium: 'max-w-page-medium',
    narrow: 'max-w-page-narrow',
    full: '',
  },

  md: {
    large: 'md:max-w-page-max',
    medium: 'md:max-w-page-medium',
    narrow: 'md:max-w-page-narrow',
    full: 'md:max-w-full',
  },

  lg: {
    large: 'lg:max-w-page-max',
    medium: 'lg:max-w-page-medium',
    narrow: 'lg:max-w-page-narrow',
    full: 'lg:max-w-full',
  },
};

export const getPageSectionWidth = (width: MaybeResponsive<PageSectionWidth> | undefined) => {
  if (!width) {
    width = 'large';
  }
  // Note! These widths exists in the tailwind.config.js file as well
  const contentWidths = { full: 2000, large: 1230, medium: 900, narrow: 670 };
  if (typeof width === 'string') {
    const relativeWidth = contentWidths[width];
    return { sm: relativeWidth, md: relativeWidth, lg: relativeWidth };
  } else {
    return {
      sm: contentWidths[width.sm || 'large'],
      md: contentWidths[width.md || 'large'],
      lg: contentWidths[width.lg || 'large'],
    };
  }
};
